import * as React from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { CheckIcon } from '../Icons';

const checkboxVariants = cva(
  'flex items-center justify-center w-[22px] h-[22px] border-2 rounded transition-colors text-white',
  {
    variants: {
      state: {
        default: 'border-[#E4E4E4] bg-white',
        selected: 'border-[#27272C] bg-[#27272C]',
        hover: 'hover:bg-[#F5F5F7]',
        disabled: 'cursor-not-allowed opacity-50',
        loading: 'cursor-not-allowed',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  },
);

export interface CheckboxProps
  extends React.ButtonHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof checkboxVariants> {
  checked?: boolean;
  loading?: boolean;
  disabled?: boolean;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      state,
      checked = false,
      loading = false,
      disabled = false,
      ...props
    },
    ref,
  ) => {
    const stateVariant = React.useMemo(() => {
      if (disabled) return 'disabled';
      if (loading) return 'loading';
      if (checked) return 'selected';
      return 'default';
    }, [disabled, loading, checked]);

    return (
      <label className="relative flex items-center cursor-pointer">
        <input
          type="checkbox"
          ref={ref}
          checked={checked}
          disabled={disabled || loading}
          className="absolute opacity-0 w-0 h-0"
          {...props}
        />
        <span
          className={cn(checkboxVariants({ state: stateVariant }), className)}
        >
          {checked && !loading && <CheckIcon />}
        </span>
      </label>
    );
  },
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
